// Date / time formatters. Captures arrive as ISO strings from the API;
// helpers accept either a Date or a millisecond epoch. The prototype
// anchored "now" against a fixed timestamp for a stable demo; we use
// real wall-clock time here.

export function toMs(value: string | number | Date): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  return Date.parse(value);
}

export function relTime(value: string | number | Date, now: number = Date.now()): string {
  const ms = toMs(value);
  const diff = now - ms;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(ms).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

export function fullTimestamp(value: string | number | Date): string {
  return new Date(toMs(value))
    .toLocaleString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(",", " ·");
}

export function dateBucket(value: string | number | Date, now: number = Date.now()): string {
  const date = new Date(toMs(value));
  const today = new Date(now);
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - 6);

  if (isSameDay(date, today)) return "today";
  if (isSameDay(date, yesterday)) return "yesterday";
  if (date >= weekStart) return "earlier this week";
  return date
    .toLocaleDateString("en-GB", { month: "long", year: "numeric" })
    .toLowerCase();
}

export interface GroupedBucket<T> {
  label: string;
  items: T[];
}

// Pinned items get their own "pinned" group at the top; the API sorts them
// first, so date-bucketing them would drag their (old) month above "today".
export function groupByBucket<T extends { captured_at: string; pinned?: number }>(
  items: T[],
  now: number = Date.now(),
): GroupedBucket<T>[] {
  const order: string[] = [];
  const map = new Map<string, T[]>();
  const pinned = items.filter((i) => i.pinned);
  if (pinned.length) {
    map.set("pinned", pinned);
    order.push("pinned");
  }
  for (const item of items) {
    if (item.pinned) continue;
    const bucket = dateBucket(item.captured_at, now);
    if (!map.has(bucket)) {
      map.set(bucket, []);
      order.push(bucket);
    }
    map.get(bucket)!.push(item);
  }
  return order.map((label) => ({ label, items: map.get(label)! }));
}
